/**
 * Página pública do Link de Pagamento — rota /c/:slug
 *
 * Layout "Modern institutional" (refator v3): card branco arredondado,
 * header gradient com emissor à esquerda e pagador à direita, card
 * flutuante de resumo da fatura, cronograma de parcelas clicáveis e
 * abas unificadas PIX / Boleto / Cartão no mesmo link.
 *
 * RELEASE: 2026-09-18 v3.0.1 — Correção de fuso na exibição do vencimento:
 *                              datas puras (YYYY-MM-DD) passam a usar
 *                              `formatDateBR`, evitando exibir o dia anterior
 *                              em America/Sao_Paulo (UTC-3).
 * RELEASE: 2026-06-21 v3.0.0 — Redesign completo no layout "Modern
 *                              institutional"; novo cronograma de
 *                              parcelas clicáveis (RPC v1.5.0) e abas
 *                              unificadas de método de pagamento.
 * (Versões anteriores v1.x/v2.x permanecem no histórico do git.)
 */


import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, Clock, Mail, Receipt } from 'lucide-react';
import { CardPaymentForm, type CardPreview } from './checkout/CardPaymentForm';
import { PixPaymentPanel } from './checkout/PixPaymentPanel';
import { BoletoPaymentPanel } from './checkout/BoletoPaymentPanel';
import { InvoiceHeader } from './checkout/components/InvoiceHeader';
import { InvoiceSummaryCard } from './checkout/components/InvoiceSummaryCard';
import {
  InstallmentsTimeline,
  type InstallmentEntry,
} from './checkout/components/InstallmentsTimeline';
import {
  PaymentMethodTabs,
  type PaymentMethod,
} from './checkout/components/PaymentMethodTabs';
import { CheckoutFooter } from './checkout/components/CheckoutFooter';
import { RecurringPaymentNotice } from './checkout/components/RecurringPaymentNotice';

import {
  pickReadableForeground,
  safeBrandColor,
  shadeHex,
} from '@/lib/checkout/brandColor';
import { useInstallmentSession } from '@/hooks/financial/useInstallmentSession';
import { formatRecurrenceLabel, type RecurrenceInterval } from '@/lib/crm/recurringServiceUtils';
// v3.0.1: utilitário central de data (trata DATE puro sem conversão de fuso)
import { formatDateBR } from '@/lib/format-date';



const logger = createLogger('CheckoutPublic');

// =========================================================================
// Tipos
// =========================================================================
interface Endereco {
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  cep?: string | null;
}

interface CustomerFull {
  display_name?: string | null;
  documento?: string | null;
  tipo?: 'pf' | 'pj' | null;
  email?: string | null;
  telefone?: string | null;
  endereco?: Endereco | null;
}

interface CheckoutSessionData {
  session_id: string;
  expires_at?: string;
  status: string;
  terminal: boolean;
  cobranca: {
    id_short?: string;
    descricao?: string;
    valor_total: number;
    moeda?: string;
    data_vencimento?: string;
    tipo_cobranca?: string;
    juros?: number;
    multa?: number;
    numero_parcelas?: number;
    numero_fatura?: string | null;
    /** v1.9.0 — status agregado da fatura (pode diferir do status do topo
     *  quando o link é de uma parcela específica). */
    status?: string;
    itens?: Array<{
      descricao: string;
      quantidade: number;
      valor_unitario: number;
      total: number;
    }>;
    /** v3.1.0 — quando a cobrança vem de uma assinatura recorrente,
     *  expõe a frequência (mensal, trimestral, etc.) para personalizar
     *  o rótulo "Recorrente - Mensal" e a mensagem por método. */
    assinatura?: { frequencia?: string | null } | null;
  };

  /** v1.9.0 — quando o link é de uma parcela específica, traz os dados
   *  dessa parcela para o header refletir valor/vencimento/status. */
  parcela?: {
    numero_parcela: number;
    total_parcelas: number;
    valor: number;
    data_vencimento: string;
    status: string;
  } | null;
  customer?: {
    nome_mascarado: string;
    cpf_mascarado: string | null;
  };
  customer_full?: CustomerFull | null;
  branding?: {
    tenant_name: string;
    logo_url: string | null;
    contact_email: string | null;
    primary_color?: string | null;
    document?: { type: 'cpf' | 'cnpj' | null; number: string | null } | null;
    phone?: string | null;
    address?: {
      cep?: string | null;
      street?: string | null;
      number?: string | null;
      complement?: string | null;
      neighborhood?: string | null;
      city?: string | null;
      state?: string | null;
    } | null;
  };
  payment_methods?: string[];
  /** v1.5.0 — lista de parcelas da mesma cobrança (vazia quando avulsa). */
  installments?: InstallmentEntry[];
  /** v1.12.0 — link do recibo do provider (Asaas `transactionReceiptUrl`).
   *  Mesmo link exibido na coluna Recibo do modal interno "Detalhes da
   *  Cobrança". Presente apenas em status terminais e quando o webhook
   *  já gravou o link. */
  receipt_url?: string | null;
  /** v1.7.0 — plano de pagamento por cartão para cobranças parceladas.
   *  Usado pelo CardPaymentForm para exibir modal de confirmação quando
   *  o tenant optou por 'pay_all_remaining'. Null quando não aplicável. */
  card_payment_plan?: {
    mode: 'pay_all_remaining' | 'pay_single_only';
    total_open_installments: number;
    total_amount: number;
    current_installment_number: number | null;
    current_installment_amount: number | null;
  } | null;
}

// =========================================================================
// Helpers
// =========================================================================
function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
}

/**
 * Formata a data de vencimento para exibição.
 * v3.0.1: delega para `formatDateBR`, que trata DATE puro (YYYY-MM-DD) sem
 * conversão de timezone — `new Date('2026-09-18')` era lido como UTC 00:00 e
 * exibia 17/09 em America/Sao_Paulo.
 */
function formatDateShort(iso?: string | null): string {
  return formatDateBR(iso);
}


/** Mapeia `payment_methods[]` do backend → enum interno das tabs. */
function normalizeMethods(raw: string[] | undefined): PaymentMethod[] {
  if (!raw || raw.length === 0) return ['pix', 'boleto'];
  const order: PaymentMethod[] = ['pix', 'boleto', 'card'];
  const seen = new Set<PaymentMethod>();
  for (const m of raw) {
    const lower = m.toLowerCase();
    if (lower === 'pix') seen.add('pix');
    else if (lower === 'boleto') seen.add('boleto');
    else if (lower === 'card' || lower === 'credit_card' || lower === 'cartao_credito') seen.add('card');
  }
  return order.filter((m) => seen.has(m));
}

// =========================================================================
// Componente principal
// =========================================================================
export default function CheckoutPublic() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<CheckoutSessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(true);
  const terminalRef = useRef(false);

  const load = useCallback(async (silent = false): Promise<void> => {
    if (!slug) {
      if (!silent) {
        setError('Link de pagamento inválido.');
        setLoading(false);
      }
      return;
    }
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke(
        'public-checkout',
        { body: { action: 'resolve', slug } },
      );
      if (!mountedRef.current) return;
      if (invokeErr) {
        let friendly: string | null = null;
        try {
          const ctx = (invokeErr as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body && typeof body.error === 'string') friendly = body.error;
          }
        } catch {
          /* ignora */
        }
        if (!silent) {
          setError(
            friendly ?? 'Estamos preparando seu link. Aguarde alguns segundos e tente novamente.',
          );
          setLoading(false);
        } else {
          logger.warn('Refetch silencioso falhou', { err: invokeErr.message });
        }
        return;
      }
      if (!resp || (resp as { error?: string }).error) {
        if (!silent) {
          setError((resp as { error?: string })?.error ?? 'Link não encontrado.');
          setLoading(false);
        }
        return;
      }
      const next = resp as CheckoutSessionData;
      terminalRef.current = Boolean(next.terminal);
      setData(next);
      if (!silent) setLoading(false);
    } catch (err) {
      logger.error('Falha ao resolver sessão pública', { err: String(err), silent });
      if (!mountedRef.current) return;
      if (!silent) {
        setError('Erro de conexão. Tente novamente em instantes.');
        setLoading(false);
      }
    }
  }, [slug]);

  useEffect(() => {
    mountedRef.current = true;
    terminalRef.current = false;
    load(false);

    const REFETCH_COOLDOWN_MS = 10_000;
    let lastRefetchAt = Date.now();
    const safeRefetch = () => {
      if (terminalRef.current) return;
      const now = Date.now();
      if (now - lastRefetchAt < REFETCH_COOLDOWN_MS) return;
      lastRefetchAt = now;
      load(true);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') safeRefetch();
    };
    const onFocus = () => safeRefetch();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  useEffect(() => {
    const brand = data?.branding?.tenant_name ?? 'Pagamento';
    document.title = `Fatura · ${brand}`;
  }, [data?.branding?.tenant_name]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 py-0 md:py-12 px-0 md:px-4 flex items-start md:items-center justify-center">
        <div className="w-full max-w-[63rem] bg-white rounded-none md:rounded-[2.5rem] shadow-none md:shadow-2xl md:shadow-slate-200/50 overflow-hidden border-0 md:border md:border-slate-200/60">
          <Skeleton className="h-48 w-full rounded-none" />
          <div className="px-3 md:px-10 -mt-6 relative z-10">
            <Skeleton className="h-44 w-full rounded-3xl" />
          </div>
          <div className="px-3 md:px-10 py-6 md:py-10 space-y-3">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center space-y-4 rounded-3xl">
          <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold">Link indisponível</h1>
          <p className="text-sm text-muted-foreground">
            {error ?? 'Esta página de pagamento não está mais disponível.'}
          </p>
        </Card>
      </div>
    );
  }

  return <ModernInvoiceLayout data={data} slug={slug ?? ''} />;
}

// =========================================================================
// Layout principal "Modern Institutional"
// =========================================================================
function ModernInvoiceLayout({ data, slug }: { data: CheckoutSessionData; slug: string }) {
  const navigate = useNavigate();
  const primaryHex = safeBrandColor(data.branding?.primary_color);
  const gradient = useMemo(
    () => ({ from: primaryHex, to: shadeHex(primaryHex, 8) }),
    [primaryHex],
  );
  const fgHex = pickReadableForeground(primaryHex);

  const cssVars = {
    '--brand-primary': primaryHex,
    '--brand-foreground': fgHex,
  } as React.CSSProperties;

  const customer = data.customer_full;
  // v1.9.0 — distinguir link de parcela vs link de fatura/avulsa.
  // `data.status` no topo agora sempre reflete o status AGREGADO da fatura
  // (cobrança). O status da parcela específica vive em `data.parcela.status`.
  const isParcelaLink = !!data.parcela;
  const parcelaPaid = data.parcela?.status === 'pago';
  const parcelaCanceled =
    data.parcela?.status === 'cancelado' || data.parcela?.status === 'estornado';
  const cobrancaStatus = data.cobranca.status ?? data.status;
  const cobrancaPaid = cobrancaStatus === 'pago';
  const cobrancaCanceled =
    cobrancaStatus === 'cancelado' || cobrancaStatus === 'estornado';

  // Estados "terminais" que substituem o formulário de pagamento:
  //  - link de parcela: usa o status DA PARCELA (não da fatura)
  //  - link de fatura/avulsa: usa o status da própria cobrança
  const isPaid = isParcelaLink ? parcelaPaid : cobrancaPaid;
  const isCanceled = isParcelaLink ? parcelaCanceled : cobrancaCanceled;
  const isExpired = data.terminal && !isPaid && !isCanceled;

  const [cardPreview, setCardPreview] = useState<CardPreview>({
    number: '',
    holder: '',
    expiry: '',
    brand: 'unknown',
    focus: null,
  });
  const [paidLocal, setPaidLocal] = useState(false);

  // v3.1.0 — quando a cobrança é recorrente, anexa a periodicidade ao
  // rótulo ("Recorrente - Mensal"). Fallback silencioso para "Recorrente"
  // quando a frequência ainda não estiver disponível no payload (compat).
  const freqRaw = data.cobranca.assinatura?.frequencia ?? null;
  const frequenciaLabel = freqRaw
    ? formatRecurrenceLabel(freqRaw as RecurrenceInterval)
    : '';
  const isRecorrente = data.cobranca.tipo_cobranca === 'recorrente';
  const tipoLabel =
    data.cobranca.tipo_cobranca === 'parcelada'
      ? `Parcelada${data.cobranca.numero_parcelas ? ` em ${data.cobranca.numero_parcelas}x` : ''}`
      : isRecorrente
        ? frequenciaLabel
          ? `Recorrente - ${frequenciaLabel}`
          : 'Recorrente'
        : 'Avulsa';

  // Valor da recorrência exibido no aviso por método de pagamento.
  // Reaproveita o `valor_total` da cobrança (já em BRL no fluxo atual).
  const valorRecorrenciaFormatado = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: data.cobranca.moeda || 'BRL',
      }).format(data.cobranca.valor_total),
    [data.cobranca.moeda, data.cobranca.valor_total],
  );


  const methods = useMemo(
    () => normalizeMethods(data.payment_methods),
    [data.payment_methods],
  );

  const { ensure: ensureInstallment, pendingParcelaId } = useInstallmentSession();

  const handleNavigateInstallment = useCallback(
    (nextSlug: string) => {
      if (!/^[a-f0-9]{32}$/.test(nextSlug)) {
        logger.warn('Slug de parcela inválido — navegação bloqueada');
        return;
      }
      navigate(`/c/${nextSlug}`);
    },
    [navigate],
  );

  const handleEnsureInstallment = useCallback(
    async (parcelaId: string): Promise<string | null> => {
      if (!slug) return null;
      return await ensureInstallment(slug, parcelaId);
    },
    [ensureInstallment, slug],
  );

  const brand = data.branding?.tenant_name ?? 'Pagamento';
  const installments = data.installments ?? [];

  return (
    <div
      className="min-h-screen bg-slate-100 py-0 md:py-12 px-0 md:px-4 flex items-start md:items-center justify-center"
      style={cssVars}
    >
      <div className="w-full max-w-[71rem] bg-white rounded-none md:rounded-[2.5rem] shadow-none md:shadow-2xl md:shadow-slate-200/50 overflow-hidden border-0 md:border md:border-slate-200/60">
        <InvoiceHeader
          seller={{
            tenant_name: brand,
            logo_url: data.branding?.logo_url ?? null,
            document: data.branding?.document,
            contact_email: data.branding?.contact_email ?? null,
            phone: data.branding?.phone ?? null,
            address: data.branding?.address,
          }}
          payer={customer}
          gradient={gradient}
        />

        {(() => {
          // v1.9.0 — quando o link é de uma PARCELA específica, o header
          // do checkout exibe valor/vencimento/status DA PARCELA. O total
          // agregado da fatura continua aparecendo no rodapé "Total" da
          // tabela de itens (totalFormatado), preservando a transparência.
          const summaryStatus = paidLocal
            ? (isParcelaLink ? 'pago_parcela' : 'pago')
            : isParcelaLink
              ? data.parcela!.status === 'pago'
                ? 'pago_parcela'
                : data.parcela!.status === 'cancelado'
                  ? 'cancelado_parcela'
                  : data.parcela!.status === 'estornado'
                    ? 'estornado_parcela'
                    : 'pendente_parcela'
              : data.status;
          const summaryTerminal = paidLocal
            ? true
            : isParcelaLink
              ? ['pago', 'cancelado', 'estornado'].includes(data.parcela!.status)
              : data.terminal;
          const summaryValor = isParcelaLink
            ? formatCurrency(data.parcela!.valor, data.cobranca.moeda)
            : formatCurrency(data.cobranca.valor_total, data.cobranca.moeda);
          const summaryVenc = isParcelaLink
            ? formatDateShort(data.parcela!.data_vencimento)
            : formatDateShort(data.cobranca.data_vencimento);
          return (
            <InvoiceSummaryCard
              status={summaryStatus}
              terminal={summaryTerminal}
              numeroFatura={data.cobranca.numero_fatura ?? null}
              valorFormatado={summaryValor}
              totalFormatado={formatCurrency(data.cobranca.valor_total, data.cobranca.moeda)}
              vencimentoFormatado={summaryVenc}
              frequencia={tipoLabel}
              descricao={data.cobranca.descricao}
              itens={data.cobranca.itens}
              numeroParcela={data.parcela?.numero_parcela ?? null}
              totalParcelas={data.parcela?.total_parcelas ?? null}
              logoUrl={data.branding?.logo_url ?? null}
              sellerName={data.branding?.tenant_name ?? null}
            />
          );
        })()}

        <InstallmentsTimeline
          installments={installments}
          currentSlug={slug}
          onNavigate={handleNavigateInstallment}
          onEnsure={handleEnsureInstallment}
          pendingParcelaId={pendingParcelaId}
        />

        {isPaid ? (
          <TerminalBlock
            variant={isParcelaLink && !cobrancaPaid ? 'paid_installment' : 'paid'}
            data={data}
          />
        ) : isCanceled ? (
          <TerminalBlock variant="canceled" data={data} />
        ) : isExpired ? (
          <TerminalBlock variant="expired" data={data} />
        ) : paidLocal ? (
          <TerminalBlock
            variant={isParcelaLink && !cobrancaPaid ? 'paid_installment' : 'paid'}
            data={data}
          />
        ) : (
          <PaymentMethodTabs
            available={methods}
            slug={slug}
            renderPix={() => (
              <>
                {isRecorrente && frequenciaLabel && (
                  <RecurringPaymentNotice
                    method="pix"
                    frequenciaLabel={frequenciaLabel}
                    valorFormatado={valorRecorrenciaFormatado}
                  />
                )}
                <PixPaymentPanel
                  slug={slug}
                  email={customer?.email ?? null}
                  onPaid={() => setPaidLocal(true)}
                />
              </>
            )}
            renderBoleto={() => (
              <>
                {isRecorrente && frequenciaLabel && (
                  <RecurringPaymentNotice
                    method="boleto"
                    frequenciaLabel={frequenciaLabel}
                    valorFormatado={valorRecorrenciaFormatado}
                  />
                )}
                <BoletoPaymentPanel slug={slug} email={customer?.email ?? null} />
              </>
            )}
            renderCard={() => (
              <CardPaymentForm
                slug={slug}
                onPaid={() => setPaidLocal(true)}
                onCardChange={setCardPreview}
                cardPaymentPlan={data.card_payment_plan ?? null}
                recurringNotice={
                  isRecorrente && frequenciaLabel ? (
                    <RecurringPaymentNotice
                      method="card"
                      frequenciaLabel={frequenciaLabel}
                      valorFormatado={valorRecorrenciaFormatado}
                    />
                  ) : null
                }
              />
            )}
          />
        )}


        
        <CheckoutFooter
          brand={brand}
          sellerDocument={
            data.branding?.document?.number
              ? `${data.branding.document.type === 'cnpj' ? 'CNPJ' : 'CPF'}: ${data.branding.document.number}`
              : ''
          }
        />
      </div>
      {/* Mantém cardPreview no DOM em escopo do form quando aplicável (CardPaymentForm gerencia render). */}
      {cardPreview && null}
    </div>
  );
}

// =========================================================================
// Blocos terminais (pago / cancelado / expirado) — renderizados no lugar
// das tabs de pagamento, mantendo o card único.
// =========================================================================
function TerminalBlock({
  variant,
  data,
}: {
  variant: 'paid' | 'paid_installment' | 'canceled' | 'expired';
  data: CheckoutSessionData;
}) {
  // v1.9.0 — variante "paid_installment": somente a parcela corrente foi
  // paga; a fatura como um todo ainda possui parcelas pendentes.
  if (variant === 'paid_installment') {
    const valorParcela = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: data.cobranca.moeda || 'BRL',
    }).format(data.parcela?.valor ?? 0);
    return (
      <section className="px-6 md:px-10 py-10 text-center space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          Parcela {data.parcela?.numero_parcela}/{data.parcela?.total_parcelas} paga
        </h2>
        <p className="text-sm text-slate-600 max-w-md mx-auto">
          Esta parcela no valor de <strong>{valorParcela}</strong> já foi paga.
          A fatura ainda possui parcelas pendentes.
        </p>
        <ReceiptActions receiptUrl={data.receipt_url ?? null} />
      </section>
    );
  }

  if (variant === 'paid') {
    return (
      <section className="px-6 md:px-10 py-10 text-center space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          Pagamento confirmado
        </h2>
        <p className="text-sm text-slate-600 max-w-md mx-auto">
          Esta fatura no valor de{' '}
          <strong>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: data.cobranca.moeda || 'BRL' }).format(data.cobranca.valor_total)}</strong>{' '}
          já foi paga. Agradecemos!
        </p>
        <ReceiptActions receiptUrl={data.receipt_url ?? null} />
      </section>
    );
  }

  if (variant === 'canceled') {
    return (
      <section className="px-6 md:px-10 py-10 text-center space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
          <AlertCircle className="w-9 h-9 text-amber-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          {data.status === 'estornado' ? 'Cobrança estornada' : 'Cobrança cancelada'}
        </h2>
        <p className="text-sm text-slate-600 max-w-md mx-auto">
          Esta fatura foi {data.status === 'estornado' ? 'estornada' : 'cancelada'} pelo
          emissor e não pode mais ser paga.
        </p>
        {data.branding?.contact_email && (
          <a
            href={`mailto:${data.branding.contact_email}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 underline"
          >
            <Mail className="w-4 h-4" /> {data.branding.contact_email}
          </a>
        )}
      </section>
    );
  }

  return (
    <section className="px-6 md:px-10 py-10 text-center space-y-4">
      <div className="mx-auto w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
        <Clock className="w-9 h-9 text-slate-600" />
      </div>
      <h2 className="text-xl md:text-2xl font-semibold text-slate-900">Link expirado</h2>
      <p className="text-sm text-slate-600 max-w-md mx-auto">
        Este link de pagamento não está mais ativo. Solicite um novo a{' '}
        <strong>{data.branding?.tenant_name ?? 'o emissor'}</strong>.
      </p>
      {data.branding?.contact_email && (
        <a
          href={`mailto:${data.branding.contact_email}`}
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 underline"
        >
          <Mail className="w-4 h-4" /> {data.branding.contact_email}
        </a>
      )}
    </section>
  );
}

// =========================================================================
// ReceiptActions — botão "Visualizar recibo" reaproveitando o link público
// do provider (Asaas `transactionReceiptUrl`) já presente em
// `metadata.transaction_receipt_url`. Sem download via bucket/Storage.
// Quando o recibo ainda não chegou pelo webhook, exibe nota discreta.
// =========================================================================
function ReceiptActions({ receiptUrl }: { receiptUrl: string | null }) {
  if (receiptUrl) {
    return (
      <div className="pt-2 flex justify-center">
        <a
          href={receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-900 shadow-sm hover:bg-slate-50 transition"
        >
          <Receipt className="w-4 h-4" />
          Visualizar recibo
        </a>
      </div>
    );
  }
  return (
    <p className="pt-1 text-xs text-slate-400">
      O recibo será disponibilizado em instantes pelo provedor de pagamento.
    </p>
  );
}

// Re-export para compat com imports legados (algumas rotas referenciam por nome)
export { ModernInvoiceLayout as PremiumInvoiceLayout };
